import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toaster/use-toast';
import { deleteAccount } from '../../store/userSlice';
import { fetchCurrentUser, fetchUserStreak } from '../../store/userSlice';
import { selectBetaFeatureEnabled, selectPaymentSystemEnabled } from '../../store/featureSlice';
import {
  Crown,
  CalendarDays,
  ArrowLeft,
  ChevronRight,
  MessageSquareText,
} from 'lucide-react';
import './UserProfile.css';
import { openBillingPortal, resumeSubscription } from '../../api/pricingApi';
import { formatPlanLabel, isPaidPlanCode, isStarterPlanCode } from '../../utils/billingPlans';
import { formatRemainingMinutesLabel, isRealtimeLimitedEntitlement } from '../../utils/realtimeEntitlements';
import { getInitials } from '../../utils/getInitials';
import { submitUserFeedback } from '../../api/userApi';
import UserFeedbackDialog from '../../components/UserFeedbackDialog/UserFeedbackDialog';
import ProfileOverview from './ProfileOverview';

function formatDate(d) {
  if (!d) return null;
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getPlanStatusLabel(sub) {
  if (!sub) return { title: 'No subscription', sub: '—' };

  const status = sub.status;
  const cancelAt = sub.cancelAt ? new Date(sub.cancelAt) : null;
  const periodEnd = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd) : null;
  const canceledAt = sub.canceledAt ? new Date(sub.canceledAt) : null;

  // Match your table:
  if (status === 'active' && !cancelAt) {
    return {
      title: 'Active',
      sub: periodEnd ? `Renews on ${formatDate(periodEnd)}` : 'Renews soon',
    };
  }

  if (status === 'active' && cancelAt) {
    return {
      title: 'Active',
      sub: `Cancels on ${formatDate(cancelAt)}`,
    };
  }

  if (status === 'canceled') {
    const ended = canceledAt || periodEnd;
    return {
      title: 'Canceled',
      sub: ended ? `Ended on ${formatDate(ended)}` : 'Ended',
    };
  }

  // Stripe statuses you might see
  if (status === 'past_due') return { title: 'Past due', sub: 'Payment issue — update billing' };
  if (status === 'trialing') return { title: 'Trial', sub: periodEnd ? `Trial ends ${formatDate(periodEnd)}` : 'Trial' };
  if (status === 'incomplete') return { title: 'Incomplete', sub: 'Finish checkout' };
  if (status === 'unpaid') return { title: 'Unpaid', sub: 'Payment failed — update billing' };

  return { title: status || '—', sub: '—' };
}

export default function UserProfile({ onBack }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();

  const user = useSelector((s) => s.user?.data);
  const streak = useSelector((s) => s.user?.streak);
  const streakLoading = useSelector((s) => s.user?.streakLoading);
  const paymentSystemEnabled = useSelector(selectPaymentSystemEnabled);
  const betaFeatureEnabled = useSelector(selectBetaFeatureEnabled);
  const [billingLoading, setBillingLoading] = useState(false);
  const [resumeHidden, setResumeHidden] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(null);
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const billing = user?.billing || null;
  const sub = billing?.subscription || null;

  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '—';
  const email = user?.email || '—';
  const memberSince = formatDate(user?.createdAt) || '—';

  const rawPlanLabel = billing?.plan?.name || billing?.plan?.code || 'Starter';
  const planCode = billing?.plan?.code || null;
  const isStarterPlan = isStarterPlanCode(planCode, rawPlanLabel);
  const isPaidPlan = isPaidPlanCode(planCode, rawPlanLabel);
  const planLabel = formatPlanLabel({ rawLabel: rawPlanLabel, planCode, betaFeatureEnabled });
  const entitlements = user?.entitlements || billing?.entitlements || null;
  const starterMinutesLeftLabel = useMemo(() => {
    if (!isStarterPlan) return null;
    if (!isRealtimeLimitedEntitlement(entitlements)) return null;
    const remaining = Number(entitlements.remainingMinutes || 0);
    const minuteWord = remaining > 0 && remaining <= 1 ? 'minute' : 'minutes';
    return formatRemainingMinutesLabel(entitlements.remainingMinutes, { suffix: `${minuteWord} left` });
  }, [entitlements, isStarterPlan]);
  const showStarterUpgrade = isStarterPlan && paymentSystemEnabled;
  const primaryPlanActionLabel = isStarterPlan ? 'Upgrade' : 'Manage';
  const primaryPlanActionClass = isStarterPlan ? 'up-btn--upgrade' : 'up-btn--plan';

  useEffect(() => {
    if (user?.id) dispatch(fetchUserStreak());
  }, [dispatch, user?.id]);

  const planStatus = useMemo(
    () => (isStarterPlan ? { title: '', sub: '' } : getPlanStatusLabel(sub)),
    [isStarterPlan, sub]
  );
  const planStatusTitle = isStarterPlan ? '' : planStatus.title;
  const planStatusTone = ['active', 'trialing'].includes(String(sub?.status || '').toLowerCase())
    ? 'active'
    : 'attention';
  const planDetail = starterMinutesLeftLabel || planStatus.sub || 'Your current plan';

  // Optional rule:
  // if user is on an org business billing account, you may not want them to manage billing personally.
  const canManageBilling =
    Boolean(billing?.billingAccountId) &&
    billing?.billingAccountType !== 'business'; // change this if you DO want org admins to manage billing here

  const finishDeleteAccount = async ({ acknowledgedBillingContinues = false } = {}) => {
    try {
      await dispatch(deleteAccount({ acknowledgedBillingContinues })).unwrap();
      toast({ title: 'Account deleted', description: 'Your account has been permanently removed.' });
      navigate('/auth', { replace: true });
    } catch (e) {
      if (e?.code === 'ACTIVE_SUBSCRIPTION_REQUIRES_ACKNOWLEDGEMENT') {
        setDeleteWarning(e.subscription || {});
        setDeleteAcknowledged(false);
        return;
      }

      toast({
        title: 'Could not delete account',
        description: String(e?.error || e?.message || e || 'Something went wrong'),
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      'Delete your account permanently? This will remove your data and cannot be undone.'
    );
    if (!confirmed) return;
    await finishDeleteAccount();
  };

  const handleManageDeleteSubscription = async () => {
    const provider = String(deleteWarning?.provider || '').toLowerCase();

    if (provider === 'app_store') {
      window.open('https://apps.apple.com/account/subscriptions', '_blank', 'noopener,noreferrer');
      return;
    }

    if (provider === 'play_store') {
      window.open('https://play.google.com/store/account/subscriptions', '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      setBillingLoading(true);
      const { url } = await openBillingPortal();
      window.location.assign(url);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Please try again later';

      toast({
        title: 'Unable to open billing',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setBillingLoading(false);
    }
  };

  const showResumeButton = useMemo(() => {
    if (resumeHidden) return false;
    if (!sub) return false;

    const now = Date.now();
    const cancelAtMs = sub.cancelAt ? new Date(sub.cancelAt).getTime() : null;
    const periodEndMs = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).getTime() : null;

    // 1) Scheduled cancel (best + common)
    if (sub.status === 'active' && cancelAtMs) return true;

    // 2) Optional: canceled but still within paid period
    if (sub.status === 'canceled' && periodEndMs && now < periodEndMs) return true;

    return false;
  }, [resumeHidden, sub]);

  const handleResume = async () => {
    try {
      setBillingLoading(true);
      await resumeSubscription();
      setResumeHidden(true);
      await dispatch(fetchCurrentUser()).unwrap();

      toast({
        title: 'Subscription resumed',
        description: 'You’re back on an active plan.',
      });

      // simplest: refetch user so UI updates
      // (assuming you have fetchCurrentUser thunk wired)
      // await dispatch(fetchCurrentUser()).unwrap();
      // If you don’t want the thunk: just reload the page as temp:
      // window.location.reload();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Please try again later';

      toast({
        title: 'Could not resume subscription',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setBillingLoading(false);
    }
  };



  const handleManageSubscription = async () => {
    if (isStarterPlan) {
      if (!paymentSystemEnabled) {
        toast({
          title: 'Billing unavailable',
          description: 'Upgrades are currently disabled.',
          variant: 'destructive',
        });
        return;
      }
      navigate('/pricing');
      return;
    }

    try {
      const { url } = await openBillingPortal();
      window.location.assign(url);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        'Please try again later';

      toast({
        title: isStarterPlan ? 'Unable to open pricing' : 'Unable to open billing',
        description: msg,
        variant: 'destructive',
      });
    }
  };

  const handleSubmitFeedback = async (feedback) => {
    await submitUserFeedback(feedback);
    toast({
      title: 'Feedback sent',
      description: 'Thank you for helping us make Telome better.',
    });
  };

  const planActionButtons = (
    <div className="up-plan-action-buttons">
      {(!isStarterPlan || showStarterUpgrade) && (
        <button
          className={`up-btn ${primaryPlanActionClass}`}
          onClick={handleManageSubscription}
          disabled={!canManageBilling || billingLoading}
          title={!canManageBilling ? 'Billing is managed by your organization' : undefined}
        >
          {primaryPlanActionLabel}
        </button>
      )}
      {showResumeButton && (
        <button
          className="up-btn up-btn--primary"
          onClick={handleResume}
          disabled={!canManageBilling || billingLoading}
          title={!canManageBilling ? 'Billing is managed by your organization' : undefined}
        >
          {billingLoading ? 'Resuming…' : 'Resume'}
        </button>
      )}
    </div>
  );

  return (
    <div className="up-root up-fade-in">
      <div className="up-profile-intro">
        <button className="up-back-button" onClick={onBack}>
          <ArrowLeft size={17} />
          Back
        </button>

        <section className="up-profile-card">
          <div className="up-avatar" aria-hidden="true">{getInitials(user)}</div>
          <div className="up-profile-copy">
            <h2>{fullName}</h2>
            <p>{email}</p>
            <div className="up-profile-member">
              <CalendarDays size={19} />
              <span>Member since {memberSince}</span>
            </div>
          </div>
        </section>
      </div>

      <div className="up-overview-grid">
        <section className={`up-plan-card ${isPaidPlan ? 'up-plan-card--paid' : 'up-plan-card--starter'}`}>
          <div className="up-plan-card__top">
            <div className="up-plan-card__identity">
              <span className="up-plan-card__icon" aria-hidden="true">
                <Crown size={25} fill="currentColor" />
              </span>
              <div>
                <h2>{planLabel} Plan</h2>
                <p>Your current plan</p>
              </div>
            </div>
            {planStatusTitle ? (
              <span className={`up-status up-status--${planStatusTone}`}>
                <span aria-hidden="true" />
                {planStatusTitle}
              </span>
            ) : null}
          </div>

          <div className="up-plan-card__bottom">
            <p>{planDetail}</p>
            {planActionButtons}
          </div>
        </section>

        <ProfileOverview
          streak={streak}
          streakLoading={streakLoading && !streak}
        />

        <button className="up-feedback-row" onClick={() => setFeedbackOpen(true)} type="button">
          <span className="up-feedback-row__icon" aria-hidden="true">
            <MessageSquareText size={22} />
          </span>
          <span className="up-feedback-row__copy">
            <strong>Give Feedback</strong>
            <span>Report a bug or suggest a feature</span>
          </span>
          <ChevronRight aria-hidden="true" size={20} />
        </button>

        <section className="up-danger-row">
          <div className="up-danger-row__copy">
            <h2>Delete account</h2>
            <p>Permanently delete your account and all data.</p>
          </div>
          <button className="up-btn up-btn--dangerGhost" onClick={handleDelete}>
            Delete
          </button>
        </section>
      </div>

      <UserFeedbackDialog
        onClose={() => setFeedbackOpen(false)}
        onSubmit={handleSubmitFeedback}
        open={feedbackOpen}
      />

      {deleteWarning && (
        <div className="up-delete-modal-backdrop" role="presentation">
          <div className="up-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-subscription-title">
            <h2 id="delete-subscription-title" className="up-delete-modal-title">
              Active subscription
            </h2>
            <p className="up-delete-modal-text">
              You are currently on {deleteWarning.planName || 'a paid plan'}. Deleting your Telome account does not automatically cancel billing through Apple, Google Play, or Stripe.
            </p>
            <p className="up-delete-modal-text">
              Cancel your subscription first to avoid future charges, or acknowledge that billing may continue before deleting your account.
            </p>

            <label className="up-delete-ack-row">
              <input
                type="checkbox"
                checked={deleteAcknowledged}
                onChange={(event) => setDeleteAcknowledged(event.target.checked)}
              />
              <span>I understand billing may continue unless I cancel with my billing provider.</span>
            </label>

            <div className="up-delete-modal-actions">
              <button className="up-btn up-btn--secondary" onClick={() => setDeleteWarning(null)}>
                Keep account
              </button>
              <button
                className="up-btn up-btn--primary"
                disabled={billingLoading}
                onClick={handleManageDeleteSubscription}
              >
                Manage subscription
              </button>
              <button
                className="up-btn up-btn--dangerGhost"
                disabled={!deleteAcknowledged}
                onClick={() => finishDeleteAccount({ acknowledgedBillingContinues: true })}
              >
                Delete anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
